/**
 * MEDIUM 6 FIX: Comprehensive Input Sanitization Utilities
 * Addresses missing input sanitization gaps în transaction processing
 */

import { VALIDATION_CONSTANTS } from '@buzunarul/shared-constants';
import { logSafeWarning } from './logSanitization';

/**
 * MEDIUM 6 FIX: HTML Sanitization - Remove HTML tags and escape special characters
 * Prevents XSS attacks from user-generated content
 */
export const sanitizeHtml = (input: string): string => {
  if (typeof input !== 'string') {
    return '';
  }
  
  // Remove HTML tags
  const htmlStripped = input.replace(/<[^>]*>/g, '');
  
  // Escape HTML special characters
  const htmlEscaped = htmlStripped
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/\//g, '&#x2F;');
  
  return htmlEscaped;
};

/**
 * MEDIUM 6 FIX: Input Normalization - Unicode normalization, case handling, diacritice
 * Ensures consistent processing of text inputs
 */
export const normalizeTextInput = (input: string): string => {
  if (typeof input !== 'string') {
    return '';
  }
  
  // Unicode normalization (NFC form)
  const normalized = input.normalize('NFC');
  
  // Trim whitespace
  const trimmed = normalized.trim();
  
  // Remove extra spaces between words
  const spacesNormalized = trimmed.replace(/\s+/g, ' ');
  
  // Remove null bytes, BOM, and other control characters
  const controlCharsRemoved = spacesNormalized.replace(/[\x00-\x1F\x7F-\x9F\uFEFF]/g, '');
  
  return controlCharsRemoved;
};

/**
 * MEDIUM 6 FIX: Safe Object Structure Validation
 * Prevents prototype pollution and validates object structure
 */
export const validateObjectStructure = (obj: unknown, expectedKeys: string[]): boolean => {
  if (!obj || typeof obj !== 'object') {
    return false;
  }
  
  // Prevent prototype pollution
  if (obj.constructor !== Object && obj.constructor !== undefined) {
    logSafeWarning('validateObjectStructure', 'Suspicious object constructor detected');
    return false;
  }
  
  // Check for prototype pollution attempts
  const dangerousKeys = ['__proto__', 'constructor', 'prototype'];
  for (const key of dangerousKeys) {
    if (key in obj) {
      logSafeWarning('validateObjectStructure', `Dangerous key detected: ${key}`);
      return false;
    }
  }
  
  // Validate that object has expected structure
  const objKeys = Object.keys(obj);
  const hasUnexpectedKeys = objKeys.some(key => !expectedKeys.includes(key));
  
  if (hasUnexpectedKeys) {
    logSafeWarning('validateObjectStructure', 'Object has unexpected keys');
    return false;
  }
  
  return true;
};

/**
 * MEDIUM 6 FIX: JSON-LD Structured Data Sanitization
 * Sanitizes structured data pentru safe JSON-LD insertion
 */
export const sanitizeStructuredData = (data: unknown): unknown => {
  if (!data || typeof data !== 'object') {
    return null;
  }
  
  // Create a deep copy to avoid mutations
  let sanitizedData: any;
  try {
    sanitizedData = JSON.parse(JSON.stringify(data));
  } catch (error) {
    logSafeWarning('sanitizeStructuredData', 'Failed to serialize structured data');
    return null;
  }
  
  // Recursively sanitize all string values
  const sanitizeValue = (value: any): any => {
    if (typeof value === 'string') {
      return sanitizeHtml(normalizeTextInput(value));
    } else if (Array.isArray(value)) {
      return value.map(sanitizeValue);
    } else if (value && typeof value === 'object') {
      const sanitizedObj: any = {};
      for (const [key, val] of Object.entries(value)) {
        // Sanitize key as well
        const sanitizedKey = sanitizeHtml(normalizeTextInput(key));
        sanitizedObj[sanitizedKey] = sanitizeValue(val);
      }
      return sanitizedObj;
    }
    return value;
  };
  
  return sanitizeValue(sanitizedData);
};

/**
 * MEDIUM 6 FIX: Cross-Field Validation for Transaction Data
 * Validates logical consistency between related fields
 */
export const validateTransactionCrossFields = (tx: any): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  if (!tx || typeof tx !== 'object') {
    return { isValid: false, errors: ['Invalid transaction object'] };
  }
  
  // Validate amount consistency
  if (tx.planned_amount !== null && tx.actual_amount !== null) {
    const plannedAmount = Number(tx.planned_amount);
    const actualAmount = Number(tx.actual_amount);
    
    if (isFinite(plannedAmount) && isFinite(actualAmount)) {
      const variance = Math.abs(plannedAmount - actualAmount);
      const percentageVariance = plannedAmount !== 0 ? (variance / Math.abs(plannedAmount)) * 100 : 0;
      
      // Flag extremely large variances (>500%) as potentially suspicious
      if (percentageVariance > 500) {
        errors.push('Extremely large variance between planned and actual amounts');
      }
    }
  }
  
  // Validate transaction type vs amount consistency
  if (tx.type && tx.amount !== null) {
    const amount = Number(tx.amount);
    if (isFinite(amount)) {
      if (tx.type === 'INCOME' && amount < 0) {
        errors.push('Income transaction cannot have negative amount');
      } else if (tx.type === 'EXPENSE' && amount > 0) {
        errors.push('Expense transaction should have negative amount');
      }
    }
  }
  
  // Validate execution status vs amount fields consistency
  if (tx.execution_status) {
    if (tx.execution_status === 'completed' && 
        (tx.actual_amount === null || tx.actual_amount === undefined)) {
      errors.push('Completed transaction must have actual_amount');
    }
    
    if (tx.execution_status === 'pending' && 
        (tx.planned_amount === null || tx.planned_amount === undefined)) {
      errors.push('Pending transaction must have planned_amount');
    }
  }
  
  return { isValid: errors.length === 0, errors };
};

/**
 * MEDIUM 6 FIX: Enhanced String Field Sanitization
 * Combines normalization, HTML sanitization, and length validation
 */
export const sanitizeStringField = (
  value: unknown, 
  fieldName: string,
  minLength: number = VALIDATION_CONSTANTS.STRINGS.MIN_DESCRIPTION_LENGTH,
  maxLength: number = VALIDATION_CONSTANTS.STRINGS.MAX_FIELD_LENGTH
): { sanitized: string; isValid: boolean; error?: string } => {
  
  if (typeof value !== 'string') {
    return { 
      sanitized: '', 
      isValid: false, 
      error: `${fieldName} must be a string` 
    };
  }
  
  // Step 1: Normalize input
  const normalized = normalizeTextInput(value);
  
  // Step 2: Sanitize HTML
  const sanitized = sanitizeHtml(normalized);
  
  // Step 3: Validate length
  if (sanitized.length < minLength) {
    return { 
      sanitized, 
      isValid: false, 
      error: `${fieldName} must be at least ${minLength} characters` 
    };
  }
  
  if (sanitized.length > maxLength) {
    return { 
      sanitized: sanitized.substring(0, maxLength), 
      isValid: false, 
      error: `${fieldName} must be at most ${maxLength} characters` 
    };
  }
  
  return { sanitized, isValid: true };
};

/**
 * MEDIUM 6 FIX: Rate Limiting for Validation Requests
 * Prevents abuse of validation endpoints
 */
class ValidationRateLimiter {
  private requests: Map<string, number[]> = new Map();
  private readonly maxRequests: number = 100; // Max requests per window
  private readonly windowMs: number = 60000; // 1 minute window
  
  isAllowed(identifier: string): boolean {
    const now = Date.now();
    const requests = this.requests.get(identifier) || [];
    
    // Clean old requests outside window
    const validRequests = requests.filter(timestamp => now - timestamp < this.windowMs);
    
    if (validRequests.length >= this.maxRequests) {
      return false;
    }
    
    // Add current request
    validRequests.push(now);
    this.requests.set(identifier, validRequests);
    
    return true;
  }
  
  reset(identifier: string): void {
    this.requests.delete(identifier);
  }
}

export const validationRateLimiter = new ValidationRateLimiter();